// App.js
import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { LanguageProvider } from './context/LanguageContext';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import { FontScaleProvider, useFontScale } from './context/FontScaleContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { DashboardDataProvider } from './context/DashboardDataContext';
import Login from './pages/Login';
import SharedDashboard from './pages/SharedDashboard';
import NoteView from './pages/NoteView';
import DashboardContainer from './components/DashboardContainer';

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="flex flex-col items-center gap-3">
        <div className="w-8 h-8 border-2 border-brand-600 border-t-transparent rounded-full animate-spin" />
        <p className="text-sm text-slate-500 font-medium">Memuat sistem...</p>
      </div>
    </div>
  );
  // Simpan URL tujuan (path + query + hash) agar bisa dikembalikan setelah login.
  if (!user) {
    const next = location.pathname + location.search + location.hash;
    // Simpan ke sessionStorage juga — lebih tahan banting daripada location.state
    // (state bisa hilang bila login memicu reload). Hanya untuk deep-link dashboard.
    if (/^\/(dashboard|note)\/.+/.test(location.pathname)) {
      try { sessionStorage.setItem('postLoginRedirect', next); } catch { /* abaikan */ }
    }
    return <Navigate to="/login" replace state={{ from: next }} />;
  }
  return children;
}

function AppRoutes() {
  const { user } = useAuth();
  return (
    <Routes>
      <Route path="/" element={<Navigate to={user ? '/dashboard' : '/login'} replace />} />
      <Route path="/login" element={<Login />} />
      {/* Halaman publik read-only — TANPA login, divalidasi token di backend */}
      <Route path="/share/:key" element={<SharedDashboard />} />
      {/* Tampilan bersih satu catatan (butuh login) — dibuka via klik kanan di daftar Notes */}
      <Route path="/note/:noteId" element={
        <PrivateRoute>
          <NoteView />
        </PrivateRoute>
      } />
      <Route path="/dashboard/*" element={
        <PrivateRoute>
          <DashboardDataProvider>
            <DashboardContainer />
          </DashboardDataProvider>
        </PrivateRoute>
      } />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// Menyelaraskan preferensi tampilan dari server (users.theme, users.font_scale)
// ke state lokal setelah user login. Diletakkan di dalam AuthProvider supaya
// semua context tersedia. Bila kolomnya masih NULL (user belum pernah memilih),
// preferensi lokal dibiarkan apa adanya.
function PrefsSync() {
  const { user } = useAuth();
  const { hydrateTheme } = useTheme();
  const { hydrateFontScale } = useFontScale();
  React.useEffect(() => { hydrateTheme(user?.theme); }, [user?.theme, hydrateTheme]);
  React.useEffect(() => { hydrateFontScale(user?.font_scale); }, [user?.font_scale, hydrateFontScale]);
  return null;
}

export default function App() {
  return (
    <ThemeProvider>
      <FontScaleProvider>
        <LanguageProvider>
          <AuthProvider>
            <PrefsSync />
            <BrowserRouter>
              <AppRoutes />
            </BrowserRouter>
          </AuthProvider>
        </LanguageProvider>
      </FontScaleProvider>
    </ThemeProvider>
  );
}
